import { getSystemDatetime } from '../../../../common/utils/DateUtils'
import './types'

/*==================================
  年度、決算年月を計算するユーティリティ関数
===================================*/

/**
 * 決算月から翌月の1日（期首日）を年度の年を考慮して計算する関数
 *
 * @param {number} fiscalYear - 年度の西暦年（例：2025）
 * @param {number} month - 決算月（1〜12）
 * @returns {string} 期首日（YYYY-MM-DD）
 */
export function calcFiscalYearStartDate(fiscalYear, month) {
  let startYear = fiscalYear
  let startMonth

  if (month === 12) {
    // 決算月が12月の場合は翌年に繰り上げず1月を指定
    startMonth = 0
  } else {
    startMonth = month
  }

  const date = new Date(startYear, startMonth, 1)

  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')

  return `${yyyy}-${mm}-${dd}`
}

/**
 * 決算月と年から決算年月（YYYY-MM-DD）を計算する共通関数
 *
 * @param {number} year - 年（西暦）
 * @param {number} month - 月（1〜12）
 * @returns {string} 決算年月の末日（YYYY-MM-DD）
 */
export function calcFiscalYearMonth(year, month) {
  const lastDay = new Date(year, month, 0).getDate()
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
}

/**
 * 決算期文字列から年度と決算年月を取得する
 *
 * @param {string} periodValue - 決算期文字列（例："2024年3月期"）
 * @param {string} accPeriod - 決算月（例："3月"）
 * @returns {{ fiscalYear: string | null; fiscalYearMonth: string | null }}
 */
export function getFiscalDateFromPeriod(periodValue, accPeriod) {
  // 決算月から月を抽出（最優先）
  const accMonthMatch = accPeriod.match(/^(\d{1,2})月/)
  const accMonth = accMonthMatch ? parseInt(accMonthMatch[1], 10) : null
  // 決算期文字列から年と月を抽出
  const yearMatch = periodValue.match(/^(\d{4})年/)
  const monthMatch = periodValue.match(/(\d{1,2})月期$/)

  if (!yearMatch) {
    return { fiscalYear: null, fiscalYearMonth: null }
  }

  const periodYear = parseInt(yearMatch[1], 10)
  // 決算月が指定あれば優先、なければperiodValueの月
  const month = accMonth !== null ? accMonth : monthMatch ? parseInt(monthMatch[1], 10) : null
  // 決算期から年度を計算: 12月なら年そのまま、それ以外は前年
  const fiscalYearNum = accMonth === 12 ? periodYear : periodYear - 1
  const fiscalYearStr = `${fiscalYearNum}年度`

  const fiscalYearMonth = calcFiscalYearMonth(periodYear, month)

  return { fiscalYear: fiscalYearStr, fiscalYearMonth }
}

/**
 * 年度文字列から年度と期首日を取得する
 *
 * @param {string} fiscalYear - 年度文字列（例："2023年度"）
 * @param {string} accPeriod - 決算月（例："3月"）
 * @returns {{ fiscalYear: string | null; fiscalYearStartMonth: string | null }}
 */
export function getFiscalStartDateFromYear(fiscalYear, accPeriod) {
  const accMonthMatch = accPeriod.match(/^(\d{1,2})月/)
  const accMonth = accMonthMatch ? parseInt(accMonthMatch[1], 10) : null
  const yearMatch = fiscalYear.match(/^(\d{4})年度$/)

  if (!accMonth || !yearMatch) {
    return { fiscalYear: null, fiscalYearStartMonth: null }
  }

  const fiscalYearNum = parseInt(yearMatch[1], 10)

  const fiscalYearStartMonth = calcFiscalYearStartDate(fiscalYearNum, accMonth)

  return { fiscalYear, fiscalYearStartMonth }
}

/**
 * 年度文字列から年度と決算年月を取得する
 *
 * @param {string} fiscalYear - 年度文字列（例："2023年度"）
 * @param {string} accPeriod - 決算月（例："3月"）
 * @returns {{ fiscalYear: string | null; fiscalYearMonth: string | null }}
 */
export function getFiscalDateFromYear(fiscalYear, accPeriod) {
  const accMonthMatch = accPeriod.match(/^(\d{1,2})月/)
  const accMonth = accMonthMatch ? parseInt(accMonthMatch[1], 10) : null
  const yearMatch = fiscalYear.match(/^(\d{4})年度$/)

  if (!accMonth || !yearMatch) {
    return { fiscalYear: null, fiscalYearMonth: null }
  }

  const fiscalYearNum = parseInt(yearMatch[1], 10)
  // 年度から決算期を計算: 12月なら年そのまま、それ以外は翌年
  const periodYear = accMonth === 12 ? fiscalYearNum : fiscalYearNum + 1

  const fiscalYearMonth = calcFiscalYearMonth(periodYear, accMonth)

  return { fiscalYear, fiscalYearMonth }
}

/**
 * 現在進行年度（YYYY年度）を取得する関数
 *
 * @param {string} accPeriod - 決算月（例："12月"）
 * @returns {string} 現在進行年度（例："2023年度"）
 */
export function getCurrentFiscalYear(accPeriod) {
  // 現在日時を取得
  const now = getSystemDatetime()

  // 現在年度の取得
  const year = now.getFullYear()
  const month = now.getMonth() + 1

  // 決算月を数値に変換
  const accMonthMatch = accPeriod.match(/^(\d{1,2})月/)
  const accMonth = accMonthMatch ? parseInt(accMonthMatch[1], 10) : null

  // 現在進行年度の計算
  if (accMonth !== 12 && month <= accMonth) {
    return `${year - 1}年度`
  }
  return `${year}年度`
}

/**
 * 年度リストを取得する関数
 *
 * @param {Object[]} annualTask - 年次タスクの配列（fiscalYearは「YYYY年度」形式）
 * @param {string} accPeriod - 決算月（例："12月"）
 * @returns {{ fiscalYear: string; fiscalYearStartMonth: string; fiscalYearMonth: string }[]}
 *   年度（YYYY年度）、期首日・決算年月（YYYY-MM-DD）の配列
 */
export function fetchFiscalYearDateList(annualTask, accPeriod) {
  // 現在進行年度取得
  const currentYear = getCurrentFiscalYear(accPeriod)
  const currentYearInt = parseInt(currentYear.replace('年度', ''), 10)

  //年度タスクの最古年度を取得
  const fiscalYears = annualTask
    .map((task) => parseInt(task.fiscalYear.replace('年度', ''), 10))
    .filter((y) => !isNaN(y))

  // 年度タスクの最古年度が取得できない場合は、現在進行年度を設定
  const minFiscalYear = fiscalYears.length > 0 ? Math.min(...fiscalYears) : currentYearInt

  // 開始年度取得
  const startYear = Math.min(minFiscalYear, currentYearInt)

  // 終了年度取得（現在進行年度 + 5年）
  const endYear = currentYearInt + 5

  // 年度リストの生成
  const fiscalYearList = []
  for (let y = endYear; y >= startYear; y--) {
    const fiscalYearDateList = getFiscalDateFromYear(`${y}年度`, accPeriod)
    const fiscalYearStartDateList = getFiscalStartDateFromYear(`${y}年度`, accPeriod)
    fiscalYearList.push({
      fiscalYear: `${y}年度`,
      fiscalYearStartMonth: fiscalYearStartDateList.fiscalYearStartMonth,
      fiscalYearMonth: fiscalYearDateList.fiscalYearMonth
    })
  }

  return fiscalYearList
}

/**
 * 決算期文字列を返却する関数
 *
 * @param {string} fiscalYear - 年度（例："2024年度"）
 * @param {string} accPeriod - 決算月（例："3月"）
 * @returns {string} 決算期（例："2025年3月期"）
 */
export function getFiscalYear(fiscalYear, accPeriod) {
  // 年度から年を取得
  const yearMatch = fiscalYear.match(/^(\d{4})年度/)
  // 決算月から月を取得
  const monthMatch = accPeriod.match(/^(\d{1,2})月/)

  const year = yearMatch ? parseInt(yearMatch[1], 10) : ''
  const month = monthMatch ? parseInt(monthMatch[1], 10) : ''

  // 決算月が12月ならその年、それ以外は翌年
  const fiscalPeriodYear = month === 12 ? year : year + 1

  // 決算期文字列「YYYY年MM月期」を返却
  return `${fiscalPeriodYear}年${month}月期`
}

/**
 * 前年を算出する関数
 *
 * @param {string} fiscalYear - 年度（例："2024年度"）
 * @returns {string} 年度（例："2023年度"）
 */
export function getPreviousFiscalYear(fiscalYear) {
  const yearMatch = fiscalYear.match(/^(\d{4})年度$/)
  if (!yearMatch) return null
  return `${parseInt(yearMatch[1], 10) - 1}年度`
}
